import { useEffect, RefObject } from 'react';

// Automatically scroll an element into view when it receives focus
// This is particularly useful on mobile when the keyboard appears and might cover the input
//
// ref - React ref to the element that should be scrolled into view
// isActive - Optional flag to enable/disable the behavior (default: true)
export function useScrollIntoView(ref: RefObject<HTMLElement>, isActive: boolean = true) {
	useEffect(() => {
		if (!ref.current || !isActive) return;

		const element = ref.current;
		let timeout1: NodeJS.Timeout | null = null;
		let timeout2: NodeJS.Timeout | null = null;

		const handleFocus = () => {
			// Small delay to allow keyboard animation to start
			timeout1 = setTimeout(() => {
				// Use scrollIntoView with smooth behavior
				element.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'nearest',
				});

				// For mobile Safari, we need to ensure the element is in view
				// after the keyboard animation completes
				timeout2 = setTimeout(() => {
					element.scrollIntoView({
						behavior: 'auto',
						block: 'center',
						inline: 'nearest',
					});
				}, 300);
			}, 100);
		};

		element.addEventListener('focus', handleFocus, { capture: true });

		return () => {
			element.removeEventListener('focus', handleFocus, { capture: true });
			// Clear any pending timeouts to prevent memory leaks
			if (timeout1) clearTimeout(timeout1);
			if (timeout2) clearTimeout(timeout2);
		};
	}, [ref, isActive]);
}
